/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSearchResultsStore } from '../modules/store/searchResultsStore';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSearchResultsStore as useShopSearchResultsStore } from '@marriott/mi-shop-components';
import { useSearchFormStore } from '../store/searchFormStore';
import { useSearchFilterStore } from '../modules/store/searchFilterZustandStore';
import {
  ConversationSearchState,
  ConversationSearchCriteria,
  DEFAULT_SEARCH_CRITERIA,
  DEFAULT_FILTER_STATE,
} from '../store';
import { DATES, ROOMS_AND_GUESTS, USE_POINTS, SPECIAL_RATES } from '../store/store.constants';

// Cache the last synced state fingerprint per conversation to avoid unnecessary syncs
const lastSyncedFingerprint = new Map<string, string>();

/**
 * Creates a comprehensive fingerprint of the conversation search state.
 * Used to detect if state has actually changed before syncing to global stores.
 * IMPORTANT: Must include ALL fields that affect component behavior.
 */
export const createStateFingerprint = (state: ConversationSearchState): string => {
  const { searchCriteria: s, filterState: f } = state;
  return [
    s?.checkInDate ?? '',
    s?.checkOutDate ?? '',
    String(s?.flexible ?? false),
    String(s?.numRooms ?? 1),
    String(s?.numAdultsPerRoom ?? 1),
    String(s?.numChildrenPerRoom ?? 0),
    (s?.childrenAges ?? []).join(','),
    String(s?.useRewardsPoints ?? false),
    s?.clusterCode ?? '',
    s?.corporateCode ?? '',
    s?.groupCode ?? '',
    (f?.brands ?? []).join(','),
    (f?.amenities ?? []).join(','),
    String(f?.priceRange?.min ?? ''),
    String(f?.priceRange?.max ?? ''),
    String(f?.pointsRange?.min ?? ''),
    String(f?.pointsRange?.max ?? ''),
    String(f?.distance ?? ''),
    (f?.hotelTypes ?? []).join(','),
    (f?.activities ?? []).join(','),
    (f?.transportationTypes ?? []).join(','),
    (f?.events ?? []).join(','),
    String(f?.includeTaxesAndFees ?? false),
  ].join('|');
};

/**
 * Creates a fingerprint from chatSearchCriteria object (used for comparison with global store state)
 */
export const createCriteriaFingerprint = (criteria: any): string => {
  if (!criteria) return '';
  return [
    criteria.checkInDate ?? '',
    criteria.checkOutDate ?? '',
    String(criteria.flexibleDate ?? false),
    String(criteria.numRooms ?? 1),
    String(criteria.numAdultsPerRoom ?? 1),
    String(criteria.numChildrenPerRoom ?? 0),
    (criteria.childrenAges ?? []).join(','),
    String(criteria.useRewardsPoints ?? false),
    criteria.clusterCode ?? '',
    criteria.corporateCode ?? '',
    criteria.groupCode ?? '',
  ].join('|');
};

/**
 * Builds search form data from conversation search criteria.
 * Maps ConversationSearchCriteria to the format expected by useSearchFormStore.
 */
const buildSearchFormData = (criteria: ConversationSearchCriteria): Record<string, any> => {
  return {
    [DATES]: {
      fromDate: criteria.checkInDate,
      toDate: criteria.checkOutDate,
      flexible: criteria.flexible ? 'true' : 'false',
      checkInDate: criteria.checkInDate,
      checkOutDate: criteria.checkOutDate,
    },
    [ROOMS_AND_GUESTS]: {
      numRooms: criteria.numRooms,
      numAdultsPerRoom: criteria.numAdultsPerRoom,
      numChildrenPerRoom: criteria.numChildrenPerRoom,
      childrenAges: criteria.childrenAges,
    },
    [USE_POINTS]: {
      rewardsRedemption: criteria.useRewardsPoints,
    },
    [SPECIAL_RATES]: {
      clusterCode: criteria.clusterCode,
      corporateCode: criteria.corporateCode,
      groupCode: criteria.groupCode,
    },
  };
};

/**
 * Builds chatSearchCriteria object from ConversationSearchCriteria.
 * This format is used by shop components (like Rates in HQV).
 */
const buildChatSearchCriteria = (criteria: ConversationSearchCriteria): any => {
  return {
    checkInDate: criteria.checkInDate,
    checkOutDate: criteria.checkOutDate,
    flexibleDate: criteria.flexible ?? false,
    numRooms: criteria.numRooms ?? 1,
    numAdultsPerRoom: criteria.numAdultsPerRoom ?? 1,
    numChildrenPerRoom: criteria.numChildrenPerRoom ?? 0,
    childrenAges: criteria.childrenAges ?? [],
    useRewardsPoints: criteria.useRewardsPoints ?? false,
    clusterCode: criteria.clusterCode ?? '',
    corporateCode: criteria.corporateCode ?? '',
    groupCode: criteria.groupCode ?? '',
  };
};

/**
 * Syncs conversation state from chat store to all dependent global stores.
 * This ensures shop library components (like Rates in HQV) receive the correct data.
 *
 * Uses fingerprint comparison to skip unnecessary syncs when state hasn't changed.
 *
 * @param chatId - The conversation ID (used for fingerprint caching)
 * @param conversationState - The conversation's search state to sync
 * @param forceSync - If true, bypasses fingerprint check and always syncs
 * @returns true if sync was performed, false if skipped
 */
export const syncConversationStateToGlobalStores = (
  chatId: string,
  conversationState: ConversationSearchState | undefined,
  forceSync = false
): boolean => {
  // If no conversation state, use defaults
  const stateToSync: ConversationSearchState = conversationState ?? {
    searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
    filterState: { ...DEFAULT_FILTER_STATE },
  };

  // Create fingerprint and check if sync is needed
  const fingerprint = createStateFingerprint(stateToSync);

  if (!forceSync && lastSyncedFingerprint.get(chatId) === fingerprint) {
    return false; // Skip sync - nothing changed
  }

  // Update fingerprint cache
  lastSyncedFingerprint.set(chatId, fingerprint);

  const { searchCriteria, filterState } = stateToSync;
  const chatSearchCriteria = buildChatSearchCriteria(searchCriteria);

  // 1. Sync to chat's searchResultsStore
  const chatStoreState = useSearchResultsStore.getState();
  const existingChatSessionDataValues = chatStoreState.sessionDataValues?.sessionDataValues ?? {};
  useSearchResultsStore.getState().setSearchResultsState(['sessionDataValues'], {
    chatSearchCriteria,
    sessionDataValues: {
      ...existingChatSessionDataValues,
      chatSearchCriteria,
    },
  });

  // 2. Sync to SHOP's searchResultsStore (for Rates component in HQV)
  const shopStoreState = useShopSearchResultsStore.getState();
  const existingShopSessionDataValues = shopStoreState.sessionDataValues?.sessionDataValues ?? {};
  useShopSearchResultsStore.getState().setSearchResultsState(['sessionDataValues'], {
    ...shopStoreState.sessionDataValues,
    chatSearchCriteria,
    sessionDataValues: {
      ...existingShopSessionDataValues,
      chatSearchCriteria,
    },
  });

  // 3. Sync to searchFormStore (for MiniSearchBar UI)
  const searchFormData = buildSearchFormData(searchCriteria);
  useSearchFormStore
    .getState()
    .setSearchFormState([DATES, ROOMS_AND_GUESTS, USE_POINTS, SPECIAL_RATES], searchFormData);

  // 4. Sync to searchFilterStore (for filter UI) - reset all filter states
  const filterStore = useSearchFilterStore.getState();

  // Build the filter state object to sync
  // This resets all filters to the conversation's state (or empty if no filters)
  const filterStateToSync: Record<string, any> = {
    // Reset all facet selections to the conversation's state
    BRANDS: {},
    AMENITIES: {},
    PROPERTY_TYPES: {},
    ACTIVITIES: {},
    TRANSPORTATION_TYPES: {},
    MEETINGS_EVENTS: {},
    POINTS: {},
    PRICE: {},
    DISTANCE: {},
  };

  // Populate with conversation's filter state if it exists
  if (filterState) {
    // Brands
    if (filterState.brands && filterState.brands.length > 0) {
      filterState.brands.forEach(brand => {
        filterStateToSync['BRANDS'][brand] = { code: brand, label: brand };
      });
    }

    // Amenities
    if (filterState.amenities && filterState.amenities.length > 0) {
      filterState.amenities.forEach(amenity => {
        filterStateToSync['AMENITIES'][amenity] = { code: amenity, label: amenity };
      });
    }

    // Hotel Types (Property Types)
    if (filterState.hotelTypes && filterState.hotelTypes.length > 0) {
      filterState.hotelTypes.forEach(type => {
        filterStateToSync['PROPERTY_TYPES'][type] = { code: type, label: type };
      });
    }

    // Activities
    if (filterState.activities && filterState.activities.length > 0) {
      filterState.activities.forEach(activity => {
        filterStateToSync['ACTIVITIES'][activity] = { code: activity, label: activity };
      });
    }

    // Transportation Types
    if (filterState.transportationTypes && filterState.transportationTypes.length > 0) {
      filterState.transportationTypes.forEach(transport => {
        filterStateToSync['TRANSPORTATION_TYPES'][transport] = { code: transport, label: transport };
      });
    }

    // Events (Meetings & Events)
    if (filterState.events && filterState.events.length > 0) {
      filterState.events.forEach(event => {
        filterStateToSync['MEETINGS_EVENTS'][event] = { code: event, label: event };
      });
    }

    // Points Range
    if (filterState.pointsRange?.min !== undefined || filterState.pointsRange?.max !== undefined) {
      const pointsKey = `${filterState.pointsRange.min ?? 0}-${filterState.pointsRange.max ?? 999999}`;
      filterStateToSync['POINTS'][pointsKey] = {
        min: filterState.pointsRange.min,
        max: filterState.pointsRange.max,
      };
    }

    // Price Range
    if (filterState.priceRange?.min !== undefined || filterState.priceRange?.max !== undefined) {
      const priceKey = `${filterState.priceRange.min ?? 0}-${filterState.priceRange.max ?? 9999}`;
      filterStateToSync['PRICE'][priceKey] = {
        min: filterState.priceRange.min,
        max: filterState.priceRange.max,
      };
    }

    // Distance
    if (filterState.distance !== undefined) {
      filterStateToSync['DISTANCE'][String(filterState.distance)] = {
        value: filterState.distance,
      };
    }
  }

  // Update the filter store with the synced state
  // This ensures filters are reset when switching conversations
  filterStore.setSearchQueryState({
    ...filterStore.searchQueryState,
    ...filterStateToSync
  });

  return true;
};

/**
 * Checks if the conversation state differs from the current global store state.
 * Used to determine if sync is needed on conversation switch.
 * IMPORTANT: Always sync when switching conversations to ensure filter state is reset.
 */
export const shouldSyncOnConversationSwitch = (
  chatId: string,
  conversationState: ConversationSearchState | undefined
): boolean => {
  // Always sync when switching conversations to ensure filter state is properly reset
  // This is critical for the points filter and other filters to be reset correctly
  const cachedFingerprint = lastSyncedFingerprint.get(chatId);

  // If no conversation state, we need to sync to reset to defaults
  if (!conversationState) {
    // Check if we've already synced defaults for this chat
    const defaultState: ConversationSearchState = {
      searchCriteria: { ...DEFAULT_SEARCH_CRITERIA },
      filterState: { ...DEFAULT_FILTER_STATE },
    };
    const defaultFingerprint = createStateFingerprint(defaultState);
    return cachedFingerprint !== defaultFingerprint;
  }

  // Create fingerprint for the target conversation state (includes all filters)
  const targetFingerprint = createStateFingerprint(conversationState);

  // If we have a cached fingerprint for this chat and it matches, no sync needed
  if (cachedFingerprint === targetFingerprint) {
    return false;
  }

  // Fingerprints don't match, sync is needed
  return true;
};

/**
 * Clears the fingerprint cache for a specific chat.
 * Call this when a chat is deleted.
 */
export const clearFingerprintCache = (chatId: string): void => {
  lastSyncedFingerprint.delete(chatId);
};

/**
 * Clears all fingerprint caches.
 * Call this on logout or when resetting the store.
 */
export const clearAllFingerprintCaches = (): void => {
  lastSyncedFingerprint.clear();
};

/**
 * Checks if a conversation search state has non-default values worth persisting.
 * Used for conditional persistence to reduce IndexedDB storage size.
 * NOTE: Only checks searchCriteria and locationState since filterState is not persisted
 * (to maintain consistency with stored TIP search results).
 */
export const hasNonDefaultState = (state?: ConversationSearchState): boolean => {
  if (!state) return false;

  const { searchCriteria: s, locationState: l } = state;

  // Check if any search criteria differs from defaults
  const hasCustomSearch = !!(
    s?.checkInDate ||
    s?.checkOutDate ||
    s?.flexible ||
    (s?.numRooms ?? 1) !== 1 ||
    (s?.numAdultsPerRoom ?? 1) !== 1 ||
    (s?.numChildrenPerRoom ?? 0) !== 0 ||
    (s?.childrenAges?.length ?? 0) > 0 ||
    s?.useRewardsPoints ||
    s?.clusterCode ||
    s?.corporateCode ||
    s?.groupCode
  );

  // Check if location state has any values
  const hasLocation = !!(l?.placeId || l?.latitude || l?.longitude || l?.destination || l?.destinationType);

  return hasCustomSearch || hasLocation;
};
